/* eslint-disable react-refresh/only-export-components -- Provider, hook, and session helpers form one public API. */
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { hasPanelCapability, type PanelCapability } from '@palacete/shared/permissions';
import { api } from './api';
import type { SessionUser } from './types';

type SessionContextValue = {
  user: SessionUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  can: (capability: PanelCapability) => boolean;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  initialUser,
  children,
}: {
  initialUser: SessionUser | null;
  children: ReactNode;
}) {
  const [user, setUser] = useState<SessionUser | null>(initialUser);
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const data = await api.auth.me();
      setUser(data.user);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void Promise.resolve().then(() => setUser(initialUser));
  }, [initialUser]);

  return (
    <SessionContext.Provider
      value={{
        user,
        loading,
        refresh,
        can: (capability) => Boolean(user && hasPanelCapability(user.role, capability)),
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
